import {
  CadesCheckKey,
  CertificateStore,
  ICadesCheck,
  ICadesObjects,
  ICadesPlugin,
  ICertificate,
  IComputerCheck,
} from './interfaces';
import { parseCertificateSubject } from './subject';

let loading: Promise<void> | undefined;

export const getCadesError = (error: unknown): string =>
  window.cadesplugin?.getLastError(error) || (error instanceof Error ? error.message : String(error));

export const loadCadesPlugin = (): Promise<void> => {
  if (!loading) {
    loading = new Promise<void>((resolve, reject) => {
      const ready = () => {
        window.clearTimeout(timeout);
        resolve();
      };
      const failed = (error: unknown) => {
        window.clearTimeout(timeout);
        reject(error);
      };
      const timeout = window.setTimeout(() => failed(new Error('Истекло время ожидания загрузки плагина')), 10000);
      const waitForPlugin = () => {
        if (!window.cadesplugin) {
          failed(new Error('cadesplugin_api.js'));
          return;
        }
        window.cadesplugin.then(ready, failed);
      };
      if (window.cadesplugin) {
        waitForPlugin();
        return;
      }
      window.cadesplugin_skip_extension_install = 1;
      window.cadesplugin_load_timeout = 10000;
      const script = document.createElement('script');
      script.src = `${process.env.PUBLIC_URL || ''}/cadesplugin_api.js`;
      script.async = true;
      script.onload = waitForPlugin;
      script.onerror = () => failed(new Error('cadesplugin_api.js'));
      document.head.appendChild(script);
    });
  }
  return loading;
};

export const checkComputer = async (storeType = CertificateStore.Personal): Promise<IComputerCheck> => {
  const checks: ICadesCheck[] = (
    ['plugin', 'pluginVersion', 'cspVersion', 'store', 'certificates', 'license'] as CadesCheckKey[]
  ).map((key) => ({ key, status: 'waiting' }));
  const result: IComputerCheck = { checks, certificates: [], ready: false };
  let current: CadesCheckKey = 'plugin';
  const set = (key: CadesCheckKey, value: Partial<ICadesCheck>) =>
    Object.assign(checks.find((item) => item.key === key) as ICadesCheck, value);
  try {
    await loadCadesPlugin();
    const plugin = window.cadesplugin as ICadesPlugin;
    set('plugin', { status: 'success' });
    current = 'pluginVersion';
    const about = await plugin.CreateObjectAsync('CAdESCOM.About');
    const version = await (await about.PluginVersion).toString();
    const [major, minor, build] = version.split('.').map(Number);
    if (!(major > 2 || (major === 2 && (minor > 0 || (minor === 0 && build >= 15000))))) {
      set('pluginVersion', { status: 'error', value: version, hint: 'pluginVersion' });
      return result;
    }
    set('pluginVersion', { status: 'success', value: version });
    current = 'cspVersion';
    set('cspVersion', { status: 'success', value: await (await about.CSPVersion()).toString() });
    current = 'store';
    const store = await plugin.CreateObjectAsync('CAdESCOM.Store');
    await store.Open(
      storeType === CertificateStore.Personal ? plugin.CAPICOM_CURRENT_USER_STORE : plugin.CADESCOM_CONTAINER_STORE,
      plugin.CAPICOM_MY_STORE,
      plugin.CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED
    );
    set('store', { status: 'success' });
    try {
      current = 'certificates';
      const certificates = await store.Certificates;
      const count = await certificates.Count;
      for (let index = 1; index <= count; index++) {
        const certificate = await certificates.Item(index);
        const subject = await certificate.SubjectName;
        const item: ICertificate = {
          certificate,
          subject,
          identity: parseCertificateSubject(subject),
          issuer: await certificate.IssuerName,
          serial: await certificate.SerialNumber,
          thumbprint: await certificate.Thumbprint,
          validFrom: await certificate.ValidFromDate,
          validTo: await certificate.ValidToDate,
          valid: false,
          hasPrivateKey: false,
          errorStatuses: [],
        };
        try {
          item.hasPrivateKey = await certificate.HasPrivateKey();
          const validity = await certificate.IsValid();
          item.valid = await validity.Result;
          try {
            const statuses = await validity.ErrorStatuses;
            for (let position = 1, length = await statuses.Count; position <= length; position++) {
              item.errorStatuses.push(await statuses.Item(position));
            }
          } catch (error) {
            item.statusError = getCadesError(error);
          }
        } catch (error) {
          item.error = getCadesError(error);
        }
        result.certificates.push(item);
      }
      set('certificates', {
        status: result.certificates.some((item) => item.valid && item.hasPrivateKey) ? 'success' : 'error',
        hint: count ? 'certificates' : 'store',
      });
    } finally {
      await store.Close();
    }
    current = 'license';
    let license: ICadesObjects['CAdESCOM.CPLicense'];
    try {
      license = await plugin.CreateObjectAsync('CAdESCOM.CPLicense');
    } catch (error) {
      set('license', { status: 'unavailable', error: getCadesError(error), hint: 'license' });
      result.ready = checks.every((check) => check.status === 'success' || check.status === 'unavailable');
      return result;
    }
    const valid = await license.IsValid();
    set('license', {
      status: valid ? 'success' : 'error',
      value: await license.ValidTo(),
      hint: valid ? undefined : 'license',
    });
    result.ready = checks.every((check) => check.status === 'success');
  } catch (error) {
    const text = getCadesError(error);
    set(current, { status: 'error', error: text, hint: text.includes('0xC2110F00') ? 'trustedSite' : current });
  }
  return result;
};

export const signBytes = async (item: ICertificate, contentBase64: string, signal?: AbortSignal): Promise<string> => {
  signal?.throwIfAborted();
  await loadCadesPlugin();
  signal?.throwIfAborted();
  const plugin = window.cadesplugin as ICadesPlugin;
  const signer = await plugin.CreateObjectAsync('CAdESCOM.CPSigner');
  signal?.throwIfAborted();
  await signer.propset_Certificate(item.certificate);
  signal?.throwIfAborted();
  await signer.propset_Options(plugin.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY);
  signal?.throwIfAborted();
  const data = await plugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
  signal?.throwIfAborted();
  await data.propset_ContentEncoding(plugin.CADESCOM_BASE64_TO_BINARY);
  signal?.throwIfAborted();
  await data.propset_Content(contentBase64);
  signal?.throwIfAborted();
  return data.SignCades(signer, plugin.CADESCOM_CADES_BES, true);
};

export const withCachedKey = async <T>(
  item: ICertificate,
  action: () => Promise<T>,
  signal?: AbortSignal
): Promise<T> => {
  signal?.throwIfAborted();
  const key = await item.certificate.PrivateKey;
  signal?.throwIfAborted();
  try {
    await key.CacheKey(true);
    return await action();
  } finally {
    await key.CacheKey(false);
  }
};

export const createProbeContent = () => {
  const bytes = window.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes));
};
